import { useState } from "react";
import { AiDifficultyLevel, Game, GameVersion, PlayMode } from "../../shared/api/client";
import BoxCard from "../../shared/components/BoxCard";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { useAuth } from "../../shared/auth/AuthContext";
import { api } from "../../shared/api/client";
import { useNavigate } from "react-router-dom";

/** Light outlines on the dark card, matching the Contact page's fields */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.35)" },
  },
  "& .MuiInputLabel-root:not(.Mui-focused)": { color: "text.secondary" },
  // The theme is light-mode, so MUI's icons and native controls default to black
  "& .MuiSelect-icon": { color: "text.secondary" },
  "& input": { colorScheme: "dark" },
};

const checkboxSx = { color: "rgba(255, 255, 255, 0.6)" };

const sectionLabelSx = { color: "text.secondary", "&.Mui-focused": { color: "text.secondary" }, mb: 0.5 };

const toggleGroupSx = {
  "& .MuiToggleButton-root": {
    color: "text.primary",
    borderColor: "rgba(255, 255, 255, 0.2)",
    px: 2,
    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
  },
  "& .MuiToggleButton-root.Mui-selected": {
    color: "primary.contrastText",
    bgcolor: "primary.main",
    "&:hover": { bgcolor: "primary.dark" },
  },
};

export function CreateMatch({
  game,
  version,
  majorVersion,
}: {
  game: Game;
  version: GameVersion;
  majorVersion?: number;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [numPlayers, setNumPlayers] = useState<number>(2);
  // Asked first: invite others, or a private game against the AI. One-player games skip the question.
  const canPlayAi = version.maxPlayers > 1 && !!version.supportsAi;
  const [playMode, setPlayMode] = useState<PlayMode>("multiplayer");
  const [aiDifficulty, setAiDifficulty] = useState<AiDifficultyLevel>("medium");
  const isAiGame = playMode === "ai" && canPlayAi;
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start each option at the game's declared default, falling back to the first choice
  // (single-select) or nothing selected (multi-select)
  const configOptions = version.configurationOptions ?? [];
  const [options, setOptions] = useState<Record<string, string | string[]>>(() => {
    const defaults: Record<string, string | string[]> = {};
    for (const opt of configOptions) {
      const ids = new Set(opt.config.choices.map((c) => c.id));
      if (opt.config.multiple) {
        const current = Array.isArray(opt.currentValue) ? opt.currentValue : [];
        defaults[opt.fieldName] = current.filter((id) => ids.has(id));
      } else if (typeof opt.currentValue === "string" && ids.has(opt.currentValue)) {
        defaults[opt.fieldName] = opt.currentValue;
      } else if (opt.config.choices.length > 0) {
        defaults[opt.fieldName] = opt.config.choices[0].id;
      }
    }
    return defaults;
  });

  const toggleChoice = (fieldName: string, choiceId: string, checked: boolean) =>
    setOptions((prev) => {
      const current = Array.isArray(prev[fieldName]) ? (prev[fieldName] as string[]) : [];
      return {
        ...prev,
        [fieldName]: checked ? [...current, choiceId] : current.filter((id) => id !== choiceId),
      };
    });

  const validate = () => {
    const errors = [];
    if (Number.isNaN(numPlayers)) {
      errors.push(`Requires a player count`);
    }
    if (numPlayers < version.minPlayers) {
      errors.push(`Requires at least ${version.minPlayers} players`);
    }
    if (numPlayers > version.maxPlayers) {
      errors.push(`At most ${version.maxPlayers} players are allowed`);
    }
    return errors;
  };

  const create = async () => {
    if (validate().length || !user) {
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await api.createMatch({
        gameId: game.id,
        numPlayers,
        visibility: "public",
        majorVersion,
        options: configOptions.length > 0 ? options : undefined,
        playMode: isAiGame ? "ai" : "multiplayer",
        aiDifficulty: isAiGame ? aiDifficulty : undefined,
      });
      // An AI game starts with every seat filled, so open the game itself; the player lives
      // outside the SPA router, hence a full navigation
      if (response.match.status === "active") {
        window.location.assign(`/player/${response.match.id}`);
      } else {
        navigate(`/match/${response.match.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create match");
      setCreating(false);
    }
  };

  return (
    <BoxCard title={`Create ${version.name} match`}>
      {canPlayAi && (
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={sectionLabelSx}>How do you want to play?</FormLabel>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={playMode}
            onChange={(_, value: PlayMode | null) => value && setPlayMode(value)}
            sx={toggleGroupSx}
          >
            <ToggleButton value="multiplayer">Invite other players</ToggleButton>
            <ToggleButton value="ai">Play against AI</ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
      )}
      <TextField
        label={isAiGame ? "Number of players (including you)" : "Number of players"}
        type="number"
        size="small"
        fullWidth
        value={Number.isNaN(numPlayers) ? "" : numPlayers}
        onChange={(e) => setNumPlayers(parseInt(e.target.value, 10))}
        slotProps={{ htmlInput: { min: version.minPlayers, max: version.maxPlayers } }}
        sx={fieldSx}
      />
      {isAiGame && numPlayers > 1 && (
        <FormControl fullWidth size="small" sx={fieldSx}>
          <InputLabel>AI strength</InputLabel>
          <Select
            value={aiDifficulty}
            label="AI strength"
            onChange={(e) => setAiDifficulty(e.target.value as AiDifficultyLevel)}
          >
            <MenuItem value="low">Easy</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">Hard</MenuItem>
          </Select>
        </FormControl>
      )}
      {configOptions.map((opt) => {
        if (opt.config.multiple) {
          const selected = Array.isArray(options[opt.fieldName])
            ? (options[opt.fieldName] as string[])
            : [];
          return (
            <FormControl key={opt.fieldName} component="fieldset">
              <FormLabel component="legend" sx={sectionLabelSx}>{opt.config.label}</FormLabel>
              <FormGroup
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  columnGap: 2,
                }}
              >
                {opt.config.choices.map((choice) => (
                  <FormControlLabel
                    key={choice.id}
                    label={choice.label}
                    sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: "0.95rem" } }}
                    control={
                      <Checkbox
                        size="small"
                        sx={checkboxSx}
                        checked={selected.includes(choice.id)}
                        onChange={(e) => toggleChoice(opt.fieldName, choice.id, e.target.checked)}
                      />
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
          );
        }
        return (
          <FormControl key={opt.fieldName} fullWidth size="small" sx={fieldSx}>
            <InputLabel>{opt.config.label}</InputLabel>
            <Select
              value={(options[opt.fieldName] as string) ?? ""}
              label={opt.config.label}
              onChange={(e) =>
                setOptions((prev) => ({
                  ...prev,
                  [opt.fieldName]: e.target.value,
                }))
              }
            >
              {opt.config.choices.map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {choice.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      })}
      {validate().map((validationError) => (
        <Typography key={validationError} sx={{ color: theme.palette.error.main }}>
          {validationError}
        </Typography>
      ))}
      {error && (
        <Typography sx={{ color: theme.palette.error.main }}>
          {error}
        </Typography>
      )}
      <Button
        variant="contained"
        onClick={create}
        disabled={validate().length > 0 || creating}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          fontWeight: 600,
          alignSelf: "flex-start",
        }}
      >
        {creating ? "Creating..." : "Create"}
      </Button>
    </BoxCard>
  );
}
